import { useCallback, useEffect, useState } from "react";
import { resolveLoginPath } from "@/lib/designPreview";
import { ApiError, api } from "./apiClient";

export interface CurrentUser {
  sub?: string;
  name?: string;
  displayName?: string;
  username?: string;
  login?: string;
  email?: string;
  upn?: string;
  groups?: string[];
  /** LDAP-Gruppen vor Dev-Rollensimulation (nur bei aktiver Simulation). */
  groups_real?: string[];
  dev_role_switch?: boolean;
  dev_simulate_birthday?: boolean;
  dev_simulate_christmas?: boolean;
  dev_simulate_halloween?: boolean;
  dev_simulate_vacation?: boolean;
  /** Serverseitiger Sicherheitsmodus (APP_SAFETY_MODE) aktiv. */
  safety_mode?: boolean;
  /** Manueller Hero-Kill-Switch (keine ausgehenden Hero-Calls). */
  hero_outbound_disabled?: boolean;
  /** Seite Meros: Büro, App-Admins oder einzeln freigegebene Monteure. */
  meros_seite?: boolean;
  [key: string]: unknown;
}

export interface HeroProfile {
  hero_partner_id?: number | null;
  hero_user_id?: number | null;
  full_name?: string | null;
  email?: string | null;
  account_type?: string | null;
  phone?: string | null;
  mobile?: string | null;
  birth_date?: string | null;
  dev_simulate_birthday?: boolean;
  match_method?: string | null;
}

/** CN=App-Admins,… → App-Admins */
export function normalizeGroupLabel(raw: string): string {
  const match = String(raw || "").match(/^CN=([^,]+)/i);
  return match?.[1] || String(raw || "").trim();
}

export function getUserDisplayName(user: CurrentUser | null): string {
  if (!user) return "";
  return (
    (user.displayName as string | undefined) ||
    (user.name as string | undefined) ||
    (user.email as string | undefined) ||
    (user.upn as string | undefined) ||
    ""
  );
}

export function normalizeEmployeeLogin(login: string): string {
  const text = String(login || "").trim().toLowerCase();
  const at = text.indexOf("@");
  return at >= 0 ? text.slice(0, at) : text;
}

/** Vergleicht gespeicherte Logins (z. B. bearbeitet_von) mit dem aktuellen Nutzer. */
export function loginsMatch(
  stored: string | null | undefined,
  current: string | null | undefined,
): boolean {
  const left = normalizeEmployeeLogin(stored || "");
  const right = normalizeEmployeeLogin(current || "");
  if (!left || !right) return false;
  return left === right;
}

export function getUserLocalLogin(user: CurrentUser | null): string {
  if (!user) return "";
  const login = String(user.login || "").trim();
  if (login) return normalizeEmployeeLogin(login);
  const username = String(user.username || user.sub || "").trim();
  if (!username) return "";
  return normalizeEmployeeLogin(username);
}

/** Kurzes Kürzel für TK/TA/MA (analog Backend ``_user_kuerzel``). */
export function userKuerzelFromUser(user: CurrentUser | null): string {
  if (!user) return "MA";
  const explicit = String(user.initials || user.kuerzel || "").trim();
  if (explicit) return explicit.toUpperCase().slice(0, 3);
  const login = getUserLocalLogin(user);
  if (login) return login.toUpperCase();
  const display = getUserDisplayName(user).trim();
  if (display) {
    const parts = display.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    }
    if (parts.length === 1) return parts[0]!.slice(0, 3).toUpperCase();
  }
  return "MA";
}

export async function fetchMeGroups(): Promise<string[]> {
  const data = await api.get<{ groups?: string[] } | string[]>("/me/groups");
  const arr = Array.isArray(data) ? data : Array.isArray(data?.groups) ? data.groups : [];
  return arr.map(normalizeGroupLabel).filter(Boolean);
}

const HERO_PROFILE_CACHE_MS = 60_000;
let heroProfileCache: { at: number; value: HeroProfile | null } | null = null;
let heroProfileInflight: Promise<HeroProfile | null> | null = null;

export async function fetchHeroProfile(force = false): Promise<HeroProfile | null> {
  if (!force && heroProfileCache && Date.now() - heroProfileCache.at < HERO_PROFILE_CACHE_MS) {
    return heroProfileCache.value;
  }
  if (!force && heroProfileInflight) return heroProfileInflight;

  heroProfileInflight = (async () => {
    try {
      const profile = await api.get<HeroProfile>("/me/hero-profile");
      heroProfileCache = { at: Date.now(), value: profile };
      return profile;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        heroProfileCache = { at: Date.now(), value: null };
        return null;
      }
      console.warn("[auth] Hero-Profil konnte nicht geladen werden:", err);
      return null;
    } finally {
      heroProfileInflight = null;
    }
  })();

  return heroProfileInflight;
}

export async function logout(): Promise<void> {
  try {
    await api.post("/auth/logout");
  } catch (err) {
    console.warn("[auth] Logout fehlgeschlagen:", err);
  } finally {
    window.location.replace(`${resolveLoginPath()}?logout=1`);
  }
}

interface UseUserState {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
}

// Auf einer Seite rufen 5–8 Komponenten (RequireAuth, Header, diverse Provider,
// die Seite selbst) parallel `useCurrentUser()` auf. Ohne Deduplizierung löst das
// ebenso viele `GET /me`-Requests aus (jeder potenziell mit LDAP-Round-Trip im
// Backend). Ein geteilter In-Flight-Promise plus kurzes TTL kollabiert das auf
// einen Request pro Navigation. Rollensimulation macht einen harten Reload und
// leert dieses Modul-State ohnehin.
const CURRENT_USER_TTL_MS = 30_000;
let currentUserCache: { at: number; value: CurrentUser } | null = null;
let currentUserInflight: Promise<CurrentUser> | null = null;

export function invalidateCurrentUserCache(): void {
  currentUserCache = null;
  currentUserInflight = null;
}

function fetchCurrentUser(force = false): Promise<CurrentUser> {
  if (!force) {
    if (currentUserCache && Date.now() - currentUserCache.at < CURRENT_USER_TTL_MS) {
      return Promise.resolve(currentUserCache.value);
    }
    if (currentUserInflight) return currentUserInflight;
  }

  const inflight = api
    .get<CurrentUser>("/me")
    .then((user) => {
      currentUserCache = { at: Date.now(), value: user };
      return user;
    })
    .finally(() => {
      if (currentUserInflight === inflight) currentUserInflight = null;
    });

  currentUserInflight = inflight;
  return inflight;
}

export function useCurrentUser(): UseUserState & { reload: () => void } {
  const [state, setState] = useState<UseUserState>(() =>
    currentUserCache
      ? { user: currentUserCache.value, loading: false, error: null }
      : { user: null, loading: true, error: null },
  );

  const load = useCallback((force: boolean) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchCurrentUser(force)
      .then((user) => setState({ user, loading: false, error: null }))
      .catch((err: unknown) => {
        // Echter Login-Redirect läuft bereits → Spinner stehen lassen, Seite
        // navigiert gleich weg.
        if (err instanceof ApiError && err.message === "redirecting-to-login") {
          return;
        }
        // Bereits angemeldet und nur ein vorübergehender Netz-/Offline-Fehler:
        // letzten bekannten Nutzer behalten, damit die App nicht ausblendet.
        if (currentUserCache) {
          setState({ user: currentUserCache.value, loading: false, error: null });
          return;
        }
        // Offline/Netzwerkfehler oder Offline-401: Fehlerzustand anzeigen statt
        // ewig im "Sitzung wird geprüft …"-Spinner zu hängen.
        const message =
          err instanceof ApiError && err.message === "offline-no-redirect"
            ? "Keine Verbindung. Bitte später erneut versuchen."
            : err instanceof Error
              ? err.message
              : "Unbekannter Fehler";
        setState({ user: null, loading: false, error: message });
      });
  }, []);

  const reload = useCallback(() => load(true), [load]);

  useEffect(() => {
    load(false);
  }, [load]);

  return { ...state, reload };
}

export function userHasGroup(user: CurrentUser | null, group: string): boolean {
  if (!user) return false;
  const groups = (user.groups || []) as string[];
  const needle = group.toLowerCase();
  return groups.some((g) => String(g).toLowerCase() === needle);
}

/** App-Admin-Check auf echten Gruppen – unabhängig von simulierter Rolle. */
export function userIsRealAppAdmin(user: CurrentUser | null): boolean {
  if (!user) return false;
  const real = user.groups_real;
  if (Array.isArray(real) && real.length > 0) {
    return userHasGroup({ groups: real }, "App-Admins");
  }
  return userHasGroup(user, "App-Admins");
}

export function userHasAnyGroup(user: CurrentUser | null, groups: string[]): boolean {
  return groups.some((g) => userHasGroup(user, g));
}

/**
 * True, wenn der Benutzer der Gruppe „Büro“ zugeordnet ist (analog zu
 * `userIsBuero()` in der klassischen App: exakte Gruppe oder Name enthält
 * „büro“/„buero“).
 */
export function userIsBuero(user: CurrentUser | null): boolean {
  if (!user) return false;
  if (userHasGroup(user, "Büro")) return true;
  return (user.groups || []).some((g) => {
    const n = String(g).toLowerCase();
    return n.includes("büro") || n.includes("buero");
  });
}

function normalizedGroupStrings(user: CurrentUser | null): string[] {
  if (!user) return [];
  return (user.groups || []).map((g) => String(g).toLowerCase());
}

export function userIsAbrechnung(user: CurrentUser | null): boolean {
  return normalizedGroupStrings(user).some((g) => g.includes("abrechnung"));
}

export function userIsAnrechnung(user: CurrentUser | null): boolean {
  return normalizedGroupStrings(user).some((g) => g.includes("anrechnung"));
}

const MANAGEMENT_GROUP_KEYS = new Set(["geschäftsführung", "geschaeftsfuehrung", "geschäftsführer", "geschaeftsfuehrer"]);

function groupNameIsManagement(normalized: string): boolean {
  if (MANAGEMENT_GROUP_KEYS.has(normalized)) return true;
  return normalized.includes("geschaeftsfuehr");
}

/** Prüft einen Gruppennamen (CN oder Kurzform) auf Geschäftsführung. */
export function isManagementGroupLabel(label: string): boolean {
  return groupNameIsManagement(normalizeGroupNameForAcl(String(label || "")));
}

function normalizeGroupNameForAcl(value: string): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");
}

/** Geschäftsführung (inkl. AD-DN-Varianten), analog zu `isManagementMember()` in index.js. */
export function userIsManagement(user: CurrentUser | null): boolean {
  if (!user) return false;
  return (user.groups || []).some((raw) => {
    const normalized = normalizeGroupNameForAcl(String(raw));
    return groupNameIsManagement(normalized);
  });
}

export function userIsMonteure(user: CurrentUser | null): boolean {
  return normalizedGroupStrings(user).some((g) => g.includes("monteur"));
}

/**
 * Artikel-/Dokumentpreise nur für Büro und App-Admins.
 * Dual-Rollen (Büro+Monteur) behalten die Preissicht – Allowlist statt Denylist.
 * Analog zu `user_can_see_prices` im Backend.
 */
export function userCanSeePrices(user: CurrentUser | null): boolean {
  if (!user) return false;
  return userIsBuero(user) || userHasGroup(user, "App-Admins");
}

/** Synchroner Peek auf den User-Cache (für Nicht-React-Helfer wie Material-Dropdowns). */
export function peekCurrentUser(): CurrentUser | null {
  return currentUserCache?.value ?? null;
}

/** Bestell-Deckblatt drucken (Büro / Monteure), analog zu bestellungen.js. */
export function userCanPrintOrderCover(user: CurrentUser | null): boolean {
  if (!user) return false;
  const groups = normalizedGroupStrings(user);
  if (!groups.length) return true;
  return groups.some(
    (g) =>
      g.includes("buero") ||
      g.includes("büro") ||
      g.includes("monteur") ||
      g.includes("montage"),
  );
}
